import React from 'react';
import { RadioButtonWithIcon } from 'Components/widgets/radio-button-with-icon/RadioButtonWithIcon';
import { ICONS } from 'Components/utils/icon/icons';
import {
  getViewIconItem,
  getOutsideFacilitiesIconItem,
  getSurfaceIconItem,
} from 'Components/widgets/accommodations/BookingAccommodationsUtils';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import { BE_TRACKING } from 'Tracking/constants';
import { addAccommodation } from 'Store/booking/proposals/proposals.action';
import { getAllProposalsIdWithAlternative } from 'Views/booking/BookingEngine.utils';
import { useFormikContext } from 'formik';
import type { LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import type {
  AccommodationFiltersFormProps,
  AlternativeFilterAccommodationWithIcon,
  BeTrackingStorage,
  PopinType,
  ReservationFilterFormValues,
} from 'Src/types/reservation';
import { ADD_ACCOMMODATION_FROM_ALTERNATIVES } from 'Store/booking/proposals/constants';
import { popinTypes } from './constants';
export const NO_VIEW_ICON_ID = 'no_view';
export const NO_OUTSIDE_FACILITY_ICON_ID = 'no_outside_facility';
export const roomTypeIcons = [
  ICONS.SINGLE_ROOM,
  ICONS.SUITE_ROOM,
  ICONS.SEPARATE_ROOMS,
  ICONS.SEPARATE_ROOMS_3,
  ICONS.CONNECTING_ROOMS,
  ICONS.SEPARATED_BUILDINGS,
  ICONS.separate_rooms,
  ICONS.SHARED_ROOM,
];
export const viewIcons = [
  ICONS.fir_tree_view,
  ICONS.pool_view,
  ICONS.mountain_view_or_slopes_view,
  ICONS.river_view,
  ICONS.sea_view,
  ICONS.seaview,
  ICONS.garden_view,
  ICONS.slopes_view,
  ICONS.mountain_view,
  ICONS.forest_view,
  ICONS.golf_view,
  ICONS.valley_view,
  ICONS.view_chailifts_or_cable_railway,
  ICONS.view_of_the_grounds,
  ICONS.view_of_the_ski_resort,
];
export const outsideFacilitiesIcons = [
  ICONS.Balcony,
  ICONS.furnished_balcony,
  ICONS.double_balcony,
  ICONS.furnished_terrace,
  ICONS.private_furnished_terrace,
  ICONS.private_garden,
  ICONS.private_pool,
  ICONS.terrace,
  ICONS.with_balcony_or_resort_view,
];
// used to identify the type of each item on accommodationRoomInfos
export const isRoomTypeItem = (itemIcon: IconName) => roomTypeIcons.includes(itemIcon);
export const isViewItem = (itemIcon: IconName) => viewIcons.includes(itemIcon);
export const isOutsideFacilityItem = (itemIcon: IconName) =>
  outsideFacilitiesIcons.includes(itemIcon);
export const isItemFilterable = (itemIcon: IconName) =>
  isRoomTypeItem(itemIcon) || isViewItem(itemIcon) || isOutsideFacilityItem(itemIcon);
type AlternativeFilterAccommodation = {
  id: string;
  label: string;
  firstAlternativeId: string;
  icon: IconName;
};

export type { PopinType };
export const addViewIconPropertiesToFilters = (
  filtersView: Array<AlternativeFilterAccommodation>,
): Array<AlternativeFilterAccommodationWithIcon> => {
  return filtersView?.map((filter) => {
    return {
      ...filter,
      icon: getViewIconItem({
        id: filter.id,
        label: filter.label,
      }),
    };
  });
};
export const addOutsideFacilitiesIconPropertiesToFilters = (
  outsideFacilitiesFilter: Array<AlternativeFilterAccommodation>,
): Array<AlternativeFilterAccommodationWithIcon> => {
  return outsideFacilitiesFilter?.map((filter) => {
    const accommodationOutsideFacility: Array<AccommodationOutsideFacility> = [
      {
        id: filter.id,
        label: filter.label,
      },
    ];
    return { ...filter, icon: getOutsideFacilitiesIconItem(accommodationOutsideFacility) };
  });
};
export const addRoomTypeIconPropertiesToFilters = (
  filtersRoomType: Array<AlternativeFilterAccommodation>,
  product: Product,
): Array<AlternativeFilterAccommodationWithIcon> => {
  return filtersRoomType?.map((filter) => {
    return { ...filter, icon: getSurfaceIconItem(filter.id, product, '') }; // filter.surface
  });
};
type AllFilters = {
  filtersView: Array<AlternativeFilterAccommodationWithIcon>;
  filtersOutsideFacilities: Array<AlternativeFilterAccommodationWithIcon>;
  filtersRoomType: Array<AlternativeFilterAccommodationWithIcon>;
};
export const getTypedFilters = (
  { filtersView, filtersOutsideFacilities, filtersRoomType }: AllFilters,
  type: PopinType,
): Array<AlternativeFilterAccommodationWithIcon> => {
  switch (type) {
    case popinTypes.VIEW:
      return filtersView || [];

    case popinTypes.OUTSIDE_FACILITIES:
      return filtersOutsideFacilities || [];

    case popinTypes.ROOM_TYPE:
      return filtersRoomType || [];

    default:
      return [];
  }
};
export type CurrentComfortAlternativesType = {
  availableFilters: any;
  id: string;
  categoryId: string;
};
type HasTypedAlternativeArgs = {
  currentComfortAlternatives?: CurrentComfortAlternativesType;
  icon: IconName;
  proposal?: Proposal | null | undefined;
};
export const hasTypedAlternative = ({
  currentComfortAlternatives,
  icon,
  proposal,
}: HasTypedAlternativeArgs) => {
  const viewAlternatives = currentComfortAlternatives?.availableFilters?.view;
  const outsideFacilityAlternatives =
    currentComfortAlternatives?.availableFilters?.outsideFacilities;
  const roomTypeAlternatives = currentComfortAlternatives?.availableFilters?.roomType;

  if (isViewItem(icon) && viewAlternatives) {
    const viewAlternativeExceptNoView = viewAlternatives.filter(({ id }) => id !== NO_VIEW_ICON_ID);
    const proposalCurrentViewId = proposal?.accommodations[0]?.view?.id;
    const pureViewAlternatives = proposalCurrentViewId
      ? viewAlternatives.filter(({ id }) => id !== proposalCurrentViewId)
      : viewAlternativeExceptNoView;
    return pureViewAlternatives.length >= 1;
  }

  if (isOutsideFacilityItem(icon) && outsideFacilityAlternatives) {
    const outsideFacilityAlternativesExceptNoOF = outsideFacilityAlternatives.filter(
      ({ id }) => id !== NO_OUTSIDE_FACILITY_ICON_ID,
    );
    const proposalCurrentOutsideFacilityId =
      proposal?.accommodations[0]?.outsideFacilities?.[0]?.id;
    // remove same id option if necessary
    const pureOutsideFacilityAlternatives = proposalCurrentOutsideFacilityId
      ? outsideFacilityAlternatives.filter(({ id }) => id !== proposalCurrentOutsideFacilityId)
      : outsideFacilityAlternativesExceptNoOF;
    return pureOutsideFacilityAlternatives.length >= 1;
  }

  if (isRoomTypeItem(icon) && roomTypeAlternatives) {
    // there is always a roomType
    const proposalCurrentRoomTypeId = proposal?.accommodations[0]?.roomType;
    // remove same id option if necessary
    const pureRoomTypeAlternatives = proposalCurrentRoomTypeId
      ? roomTypeAlternatives.filter(({ id }) => id !== proposalCurrentRoomTypeId)
      : roomTypeAlternatives;
    return pureRoomTypeAlternatives.length >= 1;
  }
};
type SubmitAccommodationArgs = {
  accommodationsSets: Array<AccommodationsSet>;
  proposal: Proposal;
  formSelectedAlternativeId: string;
  resort: Product;
  optionId: string;
  optionCategory: string;
  dispatch: ReduxDispatch;
  appContext: AppContext;
  onClose: () => void;
  trackRoomPersonalization: (action: 'add' | 'remove') => void;
};
export const submitAccommodation = ({
  accommodationsSets,
  proposal,
  formSelectedAlternativeId,
  resort,
  optionId,
  optionCategory,
  dispatch,
  appContext,
  onClose,
  trackRoomPersonalization,
}: SubmitAccommodationArgs) => {
  const selectedAccommodationSet: AccommodationsSet | null | undefined = accommodationsSets?.find(
    ({ id }) => id === formSelectedAlternativeId,
  );

  if (!formSelectedAlternativeId) {
    return onClose();
  }

  const { totalDifferentialPrice } = selectedAccommodationSet || {};
  const storage = (trackingStorageApi.get(BE_TRACKING) || {}) as BeTrackingStorage;
  const selectedProposalId = storage.contextNumber || '';
  const roomFilters = (storage.roomFilters || []).filter(
    (roomFilter) => roomFilter.proposalId === selectedProposalId,
  );
  const roomFilterIndex = roomFilters.findIndex(
    ({ upsellCategory }) => upsellCategory === optionCategory,
  );

  if (roomFilterIndex > -1) {
    const upsellRevenue = totalDifferentialPrice.value + roomFilters[roomFilterIndex].upsellRevenue;
    trackRoomPersonalization(upsellRevenue === 0 ? 'remove' : 'add');
    trackingStorageApi.set({
      ...(trackingStorageApi.get(BE_TRACKING) as BeTrackingStorage),
      roomFilters: [
        ...roomFilters.slice(0, roomFilterIndex),
        {
          upsellRevenue: upsellRevenue,
          upsellCategory: optionCategory,
          upsellName: optionId,
          proposalId: selectedProposalId,
        },
        ...roomFilters.slice(roomFilterIndex + 1),
      ],
    });
  } else {
    trackRoomPersonalization('add');
    trackingStorageApi.set({
      ...(trackingStorageApi.get(BE_TRACKING) as BeTrackingStorage),
      roomFilters: [
        ...roomFilters,
        {
          upsellRevenue: totalDifferentialPrice.value,
          upsellCategory: optionCategory,
          upsellName: optionId,
          proposalId: selectedProposalId,
        },
      ],
    });
  }

  // get the arrangement
  const arrangement = selectedAccommodationSet?.accommodations
    ?.map(({ attendeesDistribution }) => attendeesDistribution)
    ?.filter(Boolean);
  if (arrangement) {
    Promise.resolve(
      dispatch(
        addAccommodation({
          arrangement,
          globalLoaderLabel: '',
          toastLabel: '',
          isJoyview: resort.isJoyview,
          isUrbanOasis: resort.isUrbanOasis,
          proposalsId: getAllProposalsIdWithAlternative(proposal),
          appContext,
          reduxAction: ADD_ACCOMMODATION_FROM_ALTERNATIVES,
        }),
      ),
    ).then(() => onClose());
  }
};
type ChooseOptionArgs = {
  appContext: AppContext;
  formik: AccommodationFiltersFormProps;
  filters: Array<AlternativeFilterAccommodationWithIcon>;
  accommodationsSets: Array<AccommodationsSet>;
  proposal: Proposal;
  resort: Product;
  optionCategory: string;
  dispatch: ReduxDispatch;
  onClose: () => void;
  trackRoomPersonalization: (action: 'add' | 'remove') => void;
};
export const chooseOption = ({
  appContext,
  formik,
  filters,
  accommodationsSets,
  proposal,
  resort,
  optionCategory,
  dispatch,
  onClose,
  trackRoomPersonalization,
}: ChooseOptionArgs) => {
  // set the alternative
  const selectedOptionValueId = formik.values.selectedFilterValueId;
  const selectedOption = filters.find(({ id }) => id === selectedOptionValueId);

  if (selectedOption && selectedOption.firstAlternativeId) {
    const selectedOptionFirstAlternativeId = selectedOption.firstAlternativeId;
    submitAccommodation({
      accommodationsSets,
      proposal,
      formSelectedAlternativeId: selectedOptionFirstAlternativeId,
      resort,
      optionId: selectedOptionValueId,
      optionCategory,
      dispatch,
      appContext,
      onClose,
      trackRoomPersonalization,
    });
  }
};
type DefaultAccommodationFilterRadioButtonProps = {
  accommodationIconProps: LabeledIcon | null | undefined;
  value: string;
};
export const DefaultAccommodationFilterRadioButton = ({
  accommodationIconProps,
  value,
}: DefaultAccommodationFilterRadioButtonProps) => {
  const formik = useFormikContext<ReservationFilterFormValues>();
  return (
    <li className="w-150 flex-none" key={0}>
      <RadioButtonWithIcon
        name="selectedFilterValueId"
        title={accommodationIconProps?.labels?.[0] || ''}
        description={accommodationIconProps?.labels?.[1] || ''}
        value={value}
        priceDifference=""
        icon={accommodationIconProps?.icon || ICONS.TRIDENT}
        isChecked={formik?.values?.selectedFilterValueId === value}
      />
    </li>
  );
};

export const getTitle = (type: PopinType): string => {
  if (type === popinTypes.VIEW) {
    return 'booking.accommodation.roomFinder.viewFilterTitle';
  }

  if (type === popinTypes.OUTSIDE_FACILITIES) {
    return 'booking.accommodation.roomFinder.facilitiesFilterTitle';
  }

  if (type === popinTypes.ROOM_TYPE) {
    return 'booking.accommodation.roomFinder.roomConfigurationFilterTitle';
  }

  return 'booking.accommodation.roomFinder.filtersTitle';
};
type DefaultAccommodationViewRadioButtonProps = {
  defaultAccommodationViewIcon: LabeledIcon | null | undefined;
  value: string;
};
export const DefaultAccommodationViewRadioButton = ({
  defaultAccommodationViewIcon,
  value,
}: DefaultAccommodationViewRadioButtonProps) => {
  return (
    <DefaultAccommodationFilterRadioButton
      accommodationIconProps={defaultAccommodationViewIcon}
      value={value}
    />
  );
};
type DefaultAccommodationOutsideFacilitiesRadioButtonProps = {
  defaultAccommodationOutsideFacilitiesIcon: LabeledIcon | null | undefined;
  value: string;
};
export const DefaultAccommodationOutsideFacilitiesRadioButton = ({
  defaultAccommodationOutsideFacilitiesIcon,
  value,
}: DefaultAccommodationOutsideFacilitiesRadioButtonProps) => {
  return (
    <DefaultAccommodationFilterRadioButton
      accommodationIconProps={defaultAccommodationOutsideFacilitiesIcon}
      value={value}
    />
  );
};
type DefaultAccommodationRoomTypeRadioButtonProps = {
  defaultAccommodationRoomTypeIcon: LabeledIcon;
  value: string;
};
export const DefaultAccommodationRoomTypeRadioButton = ({
  defaultAccommodationRoomTypeIcon,
  value,
}: DefaultAccommodationRoomTypeRadioButtonProps) => {
  return (
    <DefaultAccommodationFilterRadioButton
      accommodationIconProps={defaultAccommodationRoomTypeIcon}
      value={value}
    />
  );
};
